import { useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardBody, CardHeader } from './ui/Card';
import { Skeleton } from './ui/States';
import { buildFormatters } from '../lib/format';
import type { Host } from '../lib/host';

/**
 * Payload shape — matches LeaderboardData composed by mcp-server-http for
 * the `list_top_brands_in_*` tools.
 */
export type LeaderboardData = {
  scope_label: string;
  from?: string;
  to?: string;
  truncated?: boolean;
  /** Brand which the caller belongs to — highlighted differently in the chart. */
  highlight_brand_id?: string | null;
  /** Project id is needed to follow-up on a drill-down click. */
  project_id?: string | null;
  items: Array<{
    brand_id: string;
    name: string;
    mention_count: number;
  }>;
};

type Persisted = { selectedBrandId?: string | null };

/**
 * Interactive leaderboard.
 *
 *   - Sorted by mention_count desc, top 10 rendered.
 *   - Clicking a row asks the host to call `get_brand_stats` for that brand,
 *     which surfaces the brand-stats widget inline in the conversation.
 *   - Selected row is persisted via host.setWidgetState so the visual focus
 *     survives a re-mount in the same chat session.
 *   - Renders empty state when items is empty (the chart axis-only view is
 *     useless and noisy).
 */
export function Leaderboard({ data, host }: { data: LeaderboardData; host: Host }) {
  const fmt = useMemo(() => buildFormatters(host.locale), [host.locale]);

  const persisted = (host.widgetState as Persisted | undefined) ?? {};
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(
    persisted.selectedBrandId ?? data.highlight_brand_id ?? null,
  );

  useEffect(() => {
    void host.setWidgetState({ selectedBrandId } satisfies Persisted);
  }, [host, selectedBrandId]);

  const sorted = useMemo(() => [...data.items].sort((a, b) => b.mention_count - a.mention_count), [data.items]);
  const top = sorted.slice(0, 10);
  const max = top[0]?.mention_count ?? 1;

  const onPickBrand = (brandId: string) => {
    setSelectedBrandId(brandId);
    void host.callTool('get_brand_stats', {
      brand_id: brandId,
      from: data.from,
      to: data.to,
    });
  };

  if (data.items.length === 0) {
    return (
      <Card>
        <CardHeader title="Top brands mentioned" subtitle={`${data.scope_label} · ${fmt.range(data.from, data.to)}`} />
        <CardBody>
          <div className="text-sm text-secondary leading-relaxed">
            No brand mentions in this window. Try a broader date range or a different scope.
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Top brands mentioned"
        subtitle={`${data.scope_label} · ${fmt.range(data.from, data.to)}`}
        right={
          data.truncated ? (
            <span
              className="text-[10px] uppercase tracking-wider text-warning bg-accent-light border border-border-light px-2 py-1 rounded"
              title="The leaderboard scan cap was hit; counts are a strict lower bound."
            >
              partial
            </span>
          ) : null
        }
      />
      <CardBody className="space-y-4">
        <div
          className="h-48 w-full motion-safe:transition-opacity motion-reduce:transition-none"
          role="img"
          aria-label={`Bar chart of top ${top.length} brands by mention count.`}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={top} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <XAxis type="number" axisLine={false} tickLine={false} domain={[0, max]} hide />
              <YAxis
                dataKey="name"
                type="category"
                width={120}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
              />
              <Tooltip
                cursor={{ fill: 'var(--color-surface-hover)', opacity: 0.5 }}
                contentStyle={{
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border-light)',
                  borderRadius: '0.5rem',
                  color: 'var(--color-text-primary)',
                  fontSize: '12px',
                }}
                formatter={(value: number) => [fmt.number(value), 'mentions']}
              />
              <Bar dataKey="mention_count" radius={[0, 4, 4, 0]} barSize={18}>
                {top.map(item => {
                  const isSelected = item.brand_id === selectedBrandId;
                  return (
                    <Cell
                      key={item.brand_id}
                      fill={isSelected ? 'var(--color-accent)' : 'var(--color-info)'}
                      fillOpacity={isSelected ? 1 : 0.7}
                    />
                  );
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <RankTable items={top} selectedId={selectedBrandId} format={fmt} onPick={onPickBrand} />
      </CardBody>
    </Card>
  );
}

function RankTable({
  items,
  selectedId,
  format,
  onPick,
}: {
  items: LeaderboardData['items'];
  selectedId: string | null;
  format: ReturnType<typeof buildFormatters>;
  onPick: (brandId: string) => void;
}) {
  return (
    <div className="border-t border-border -mx-5 -mb-4 px-2 pt-3 pb-3">
      <table
        className="w-full border-collapse text-sm"
        aria-label="Ranked list of brands. Click a row to open its stats."
      >
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-muted font-medium">
            <th scope="col" className="text-left py-1 pl-3 w-8">
              #
            </th>
            <th scope="col" className="text-left py-1">
              Brand
            </th>
            <th scope="col" className="text-right py-1 pr-3">
              Mentions
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => {
            const isSelected = item.brand_id === selectedId;
            return (
              <tr
                key={item.brand_id}
                tabIndex={0}
                role="button"
                aria-pressed={isSelected}
                aria-label={`Open ${item.name} stats — rank ${idx + 1}, ${format.number(item.mention_count)} mentions`}
                onClick={() => onPick(item.brand_id)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onPick(item.brand_id);
                  }
                }}
                className={`group cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-accent rounded ${
                  isSelected ? 'bg-accent-light' : 'hover:bg-surface-hover'
                }`}
              >
                <td className={`tabular-nums py-1.5 pl-3 ${isSelected ? 'text-accent font-medium' : 'text-muted'}`}>
                  {idx + 1}
                </td>
                <td
                  className={`truncate py-1.5 ${isSelected ? 'text-primary font-medium' : 'text-secondary group-hover:text-primary'}`}
                >
                  {item.name}
                  {isSelected && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-accent">selected</span>
                  )}
                </td>
                <td
                  className={`tabular-nums text-right py-1.5 pr-3 ${
                    isSelected ? 'text-primary font-medium' : 'text-secondary'
                  }`}
                >
                  {format.number(item.mention_count)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* Exposed for the demo route + smoke test so they can render the loading shape
 * without owning the skeleton implementation. */
export const LeaderboardSkeleton = () => <Skeleton shape="leaderboard" />;
